/**
 * multi-select.js
 */

import React from 'react';
import {
  darken,
  useMediaQuery,
  InputAdornment,
  TextField,
  Typography,
  List,
  ListSubheader,
  MenuItem,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import usePrevious from 'utils/use-previous';
import { UserContext } from 'contexts/user/user-context';
import Icon from 'components/icon';
import Popover from 'components/popover';


const useStyles = makeStyles((theme) => ({
  pointer: {
    cursor: 'pointer',
    '& .MuiInputBase-input, .MuiInputBase-root': {
      cursor: 'pointer'
    }
  },
  popper: {
    '& .MuiPaper-root': {
      minWidth: theme.spacing(34),
      maxWidth: '100%',
      margin: 0,
      [theme.breakpoints.down('sm')]: {
        left: '8px !important',
        width: 'calc(100% - 16px)'
      },
    },
    '& .MuiList-root': {
      paddingTop: 0
    },
    // [theme.breakpoints.down('sm_md')]: {
    //   width: 'calc(100% - 32px)',
    //   height: '100vh',
    //   display: 'flex',
    //   flexDirection: 'column',
    //   '& .MuiAutocomplete-popper': {
    //     flex: 1,
    //     display: 'flex',
    //     flexDirection: 'column',
    //     overflow: 'hidden',
    //   },
    //   '& .MuiAutocomplete-paper': {
    //     display: 'flex',
    //     flexDirection: 'column',
    //   },
    //   '& .MuiAutocomplete-listbox': {
    //     flex: 1,
    //     maxHeight: 'inherit',
    //   }
    // }
  },
  // popperDisablePortal: {
  //   position: 'relative',
  //   width: 'auto !important',
  // },
  input: {
    width: '100%',
    '& .MuiInputBase-root': {
      paddingRight: 0
    }
  },
  closeButton: {
    [theme.breakpoints.up('sm_md')]: {
      display: 'none'
    }
  },
  options: {
    maxHeight: theme.spacing(60),
    overflow: 'auto',
    marginTop: theme.spacing(1),
  },
  header: {
    padding: '8px 16px',
    backgroundColor: theme.palette.primary.colors[5],
    color: theme.palette.grey.colors[10],
    lineHeight: `inherit`,
    '&:hover': {
      backgroundColor: darken(theme.palette.primary.colors[5], 0.1),
      cursor: 'pointer',
    }
  },
  option: {
    flex: 1,
    justifyContent: 'space-between',
    padding: '6px 16px',
  },
  checkmark: {
    color: theme.palette.success.dark,
    marginLeft: theme.spacing(2),
    visibility: props => props.isSelected ? 'visible' : 'hidden'
  },
}));


const ListOption = (props) => {
  const {
    label,
    isHeader,
    onClick,
    isSelected,
    disabled
  } = props;
  const classes = useStyles({ isSelected });
  const Component = isHeader ? ListSubheader : MenuItem;


  return (
    <Component
      className={`flex-hc ${classes.option} ${isHeader ? classes.header : ''}`}
      onClick={() => onClick(props)}
      disabled={disabled}
    >
      <Typography>{label}</Typography>
      {!isHeader &&
      <Icon
        type='checkmark'
        className={classes.checkmark}
      />}
    </Component>
  );
}


export default function MultiSelect(props) {
  const {
    id,
    label,
    nounPlural,
    options,
    value,
    onChange,
    disabled,
  } = props;
  const classes = useStyles();
  const { user } = React.useContext(UserContext);
  const fullScreenPopper = useMediaQuery(theme => theme.breakpoints.down('sm_md'));
  const makeSortedIdString = (handlerIds) => handlerIds.sort().join('-');
  const [searchText, setSearchText] = React.useState();
  const [triggerClose, setTriggerClose] = React.useState(0);
  const [selectedIds, setSelectedIds] = React.useState(value || []);
  const [selectedIdString, setSelectedIdString] = React.useState('');
  const valueIdString = React.useMemo(() => makeSortedIdString(value), [value]);
  const prevValueIdString = usePrevious(valueIdString);
  const allLabel = `All${nounPlural ? ` ${nounPlural}` : ''}`;
  const flattenedOptions = React.useMemo(() => {
    const allOptions = options.reduce((val, o) => o.options ? [...val, ...o.options] : [...val, o], []);
    return [...new Map(allOptions.map(o => [o.id, o])).values()];
  }, [options]);
  const allOptions = React.useMemo(() => {
    return searchText
      ? flattenedOptions.filter(o => o.label.toLowerCase().includes(searchText?.toLowerCase()))
      : options.reduce((val, o) => [
        ...val, 
        o, 
        ...(o?.options?.map(o2 => ({ ...o2, headerId: o.id })) || [])
      ], []);
  }, [options, flattenedOptions, searchText]);


  // update selected ids when value changes (and is different from current value)
  React.useEffect(() => {
    if (value && valueIdString !== prevValueIdString && valueIdString !== selectedIdString) {
      setSelectedIds(value);
      setSelectedIdString(valueIdString);
    }
  }, [value, valueIdString, prevValueIdString, selectedIdString]);


  const onClose = () => {
    if (onChange) {
      // only call onchange if we have actually changed from initial input value
      if (selectedIdString !== valueIdString) {
        onChange(selectedIds);
      }
    }
    setSearchText('');
  }


  const onClickAll = () => {
    setSelectedIds([]);
    setSelectedIdString('');
    setTriggerClose(p => p + 1);
  }


  const onClickOption = (option) => {
    if (option.isHeader) {
      const newIds = [...new Set([...selectedIds, ...option.options.map(o => o.id)])];
      setSelectedIds(newIds);
      setSelectedIdString(makeSortedIdString(newIds));
    } else {
      const newIds = selectedIds.includes(option.id)
        ? selectedIds.filter(id => id !== option.id)
        : [...selectedIds, option.id];
      setSelectedIds(newIds);
      setSelectedIdString(makeSortedIdString(newIds));
    }
  }


  return (
    <>
      <Popover
        id={id}
        label={(isOpen, onClick, arrowIcon) => {
          const labelRaw = <TextField
            label={label}
            variant='outlined'
            size='small'
            value={`${selectedIds.length === 0
              ? allLabel
              : selectedIds.length > 1
                ? `${selectedIds.length} ${nounPlural}`
                : selectedIds[0] === user.id
                  ? 'Me'
                  : allOptions?.find(o => o.id === +selectedIds[0] && !o.isHeader)?.label}`}
            InputProps={{
              readOnly: true,
              endAdornment: <InputAdornment
                position='end'
                style={{ marginRight: 8 }}
                children={arrowIcon}
              />
            }}
          />;
          const labelCopy = React.Children.map(labelRaw, child => React.cloneElement(child, {
            className: `${child.props?.className || ''} ${!disabled ? classes.pointer : ''}`,
            onClick: !disabled ? onClick : null,
            disabled
          }));
          return labelCopy;
        }}
        className={classes.popper}
        onClose={onClose}
        transformOrigin={{ vertical: 'top', horizontal: 8 }}
        triggerClose={triggerClose}
      >
        <TextField
          variant='outlined'
          size={fullScreenPopper ? 'medium' : 'small'}
          className={classes.input}
          autoFocus
          value={searchText || ''}
          onChange={e => setSearchText(e.target.value)}
        />
        <div className={classes.options}>
          <List>
            {/* SELECT ALL OPTION */}
            {!searchText &&
            <ListOption 
              label={<><b>{allLabel}</b></>}
              onClick={onClickAll}
            />}

            {/* NO RESULTS */}
            {allOptions.length === 0 &&
            <ListOption
              label={<><b>No Results</b></>}
              disabled
            />}
            
            {/* OPTIONS */}
            {allOptions.map(o => (
              <ListOption
                {...o}
                key={o.isHeader ? `header-${o.id}` : `option-${o.id}-header-${o.headerId}`}
                onClick={() => onClickOption(o)}
                isSelected={o.isHeader ? false : selectedIds.includes(o.id)}
              />
            ))}
          </List>
        </div>
      </Popover>
    </>
  );
}